/**
 * EmailComposer - E-Mail verfassen / antworten
 * Unterstützt: Neue E-Mail, Reply, CC, Anhänge (PDF/DOCX), Vorlagen
 */

import { useState, useEffect, useRef } from 'react';
import {
  Mail, Send, Paperclip, X, Loader2, FileText, ChevronDown,
  Reply, Plus, BookTemplate, Users, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'image/png', 'image/jpeg',
];

export default function EmailComposer({ replyTo, onClose, onSent, klientId }) {
  const [an, setAn] = useState(replyTo?.absender || '');
  const [cc, setCc] = useState('');
  const [betreff, setBetreff] = useState(
    replyTo ? `Re: ${(replyTo.titel || '').replace(/^Re:\s*/i, '')}` : ''
  );
  const [text, setText] = useState(
    replyTo
      ? `\n\n--- Ursprüngliche Nachricht ---\nVon: ${replyTo.absender || ''}\nDatum: ${replyTo.email_datum || replyTo.created_date || ''}\nBetreff: ${replyTo.titel || ''}\n\n${replyTo.inhalt || ''}`
      : ''
  );
  const [kontoId, setKontoId] = useState('');
  const [konten, setKonten] = useState([]);
  const [anhaenge, setAnhaenge] = useState([]);
  const [vorlagen, setVorlagen] = useState([]);
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [signatur, setSignatur] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    loadKonten();
    loadVorlagen();
    loadSignatur();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadKonten = async () => {
    try {
      const res = await fetch(`${API}/api/komm/email-konten`);
      if (res.ok) {
        const data = await res.json();
        setKonten(data);
        if (data.length > 0 && !kontoId) setKontoId(data[0].id);
      }
    } catch (e) { console.error(e); }
  };

  const loadSignatur = async () => {
    try {
      const res = await fetch(`${API}/api/komm/signatur`);
      if (res.ok) {
        const data = await res.json();
        if (data.aktiv && data.text) {
          setSignatur(data);
        }
      }
    } catch (e) { console.error(e); }
  };

  const loadVorlagen = async () => {
    try {
      const res = await fetch(`${API}/api/komm/email-vorlagen`);
      if (res.ok) {
        const data = await res.json();
        setVorlagen(data.vorlagen || []);
      }
    } catch (e) { console.error(e); }
  };

  const handleFileAdd = (files) => {
    for (const file of files) {
      if (file.size > MAX_ATTACHMENT_SIZE) {
        toast.error(`${file.name}: Zu groß (max. 10 MB)`);
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = reader.result.split(',')[1];
        setAnhaenge(prev => [...prev, {
          name: file.name,
          data: b64,
          content_type: file.type || 'application/octet-stream',
          size: file.size,
        }]);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAnhang = (idx) => {
    setAnhaenge(prev => prev.filter((_, i) => i !== idx));
  };

  const applyVorlage = async (vorlageId) => {
    try {
      const res = await fetch(`${API}/api/vorlagen/${vorlageId}`);
      if (res.ok) {
        const v = await res.json();
        const tpl = v.template || v.inhalt || '';
        setText(prev => tpl + (prev ? '\n\n' + prev : ''));
        if (v.name && !betreff) setBetreff(v.name);
        toast.success(`Vorlage "${v.name}" eingefügt`);
      }
    } catch (e) { toast.error('Vorlage laden fehlgeschlagen'); }
  };

  const handleSend = async () => {
    if (!an.trim()) { toast.error('Empfänger fehlt'); return; }
    if (!betreff.trim()) { toast.error('Betreff fehlt'); return; }

    setSending(true);
    try {
      const res = await fetch(`${API}/api/komm/send/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          konto_id: kontoId || undefined,
          an: an.trim(),
          cc: cc.trim() || undefined,
          betreff: betreff.trim(),
          text: text,
          reply_to_message_id: replyTo?.email_message_id || undefined,
          klient_id: klientId || replyTo?.klient_id || undefined,
          anhaenge: anhaenge.map(a => ({
            name: a.name, data: a.data, content_type: a.content_type,
          })),
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'E-Mail gesendet');
        onSent?.();
        onClose?.();
      } else {
        toast.error(data.error || 'Versand fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Verbindungsfehler');
    }
    setSending(false);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Card className="border-purple-200 shadow-lg" data-testid="email-composer">
      <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {replyTo ? <Reply className="w-4 h-4 text-purple-600" /> : <Mail className="w-4 h-4 text-purple-600" />}
            {replyTo ? 'Antwort verfassen' : 'Neue E-Mail'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="composer-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        {/* Absender-Konto */}
        {konten.length > 0 ? (
          <div className="flex items-center gap-2">
            <Label className="w-16 text-xs text-slate-500 shrink-0">Von:</Label>
            <Select value={kontoId} onValueChange={setKontoId}>
              <SelectTrigger className="h-8 text-sm" data-testid="composer-from">
                <SelectValue placeholder="Konto wählen" />
              </SelectTrigger>
              <SelectContent>
                {konten.map(k => (
                  <SelectItem key={k.id} value={k.id}>
                    {k.from_name || k.name} &lt;{k.username}&gt;
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-xs text-amber-700">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            Kein E-Mail-Konto konfiguriert — bitte unter Administration &gt; Kommunikation einrichten
          </div>
        )}

        {/* Empfänger */}
        <div className="flex items-center gap-2">
          <Label className="w-16 text-xs text-slate-500 shrink-0">An:</Label>
          <Input
            value={an} onChange={e => setAn(e.target.value)}
            placeholder="empfaenger@example.de"
            className="h-8 text-sm"
            data-testid="composer-to"
          />
          {!showCc && (
            <Button variant="ghost" size="sm" className="text-xs px-2 h-7 text-slate-400" onClick={() => setShowCc(true)}>
              CC
            </Button>
          )}
        </div>

        {/* CC */}
        {showCc && (
          <div className="flex items-center gap-2">
            <Label className="w-16 text-xs text-slate-500 shrink-0">CC:</Label>
            <Input
              value={cc} onChange={e => setCc(e.target.value)}
              placeholder="cc@example.de (kommagetrennt)"
              className="h-8 text-sm"
              data-testid="composer-cc"
            />
          </div>
        )}

        {/* Betreff */}
        <div className="flex items-center gap-2">
          <Label className="w-16 text-xs text-slate-500 shrink-0">Betreff:</Label>
          <Input
            value={betreff} onChange={e => setBetreff(e.target.value)}
            placeholder="Betreff der E-Mail"
            className="h-8 text-sm"
            data-testid="composer-subject"
          />
        </div>

        {/* Toolbar: Vorlagen + Anhänge */}
        <div className="flex items-center gap-1.5 border-b border-slate-100 pb-2">
          {vorlagen.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" data-testid="composer-templates">
                  <BookTemplate className="w-3.5 h-3.5" /> Vorlage <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-60 overflow-auto">
                {vorlagen.map(v => (
                  <DropdownMenuItem key={v.id} onClick={() => applyVorlage(v.id)} data-testid={`vorlage-${v.id}`}>
                    <FileText className="w-3.5 h-3.5 mr-2 text-purple-500" />
                    <span className="text-sm">{v.name}</span>
                    {v.kategorie && <Badge variant="outline" className="ml-2 text-[10px]">{v.kategorie}</Badge>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            variant="ghost" size="sm" className="h-7 text-xs gap-1"
            onClick={() => fileRef.current?.click()}
            data-testid="composer-attach"
          >
            <Paperclip className="w-3.5 h-3.5" /> Anhang
          </Button>
          <input
            ref={fileRef} type="file" multiple
            accept=".pdf,.docx,.doc,.png,.jpg,.jpeg"
            className="hidden"
            onChange={e => { handleFileAdd(Array.from(e.target.files)); e.target.value = ''; }}
          />
        </div>

        {/* Anhänge-Liste */}
        {anhaenge.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="composer-attachments">
            {anhaenge.map((a, i) => (
              <Badge key={i} variant="secondary" className="flex items-center gap-1 pl-2 pr-1 py-1">
                <FileText className="w-3 h-3 text-purple-500" />
                <span className="text-xs max-w-[120px] truncate">{a.name}</span>
                <span className="text-[10px] text-slate-400">{formatSize(a.size)}</span>
                <button onClick={() => removeAnhang(i)} className="ml-0.5 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Text */}
        <Textarea
          value={text} onChange={e => setText(e.target.value)}
          placeholder="Nachricht eingeben..."
          className="min-h-[200px] text-sm resize-y font-mono"
          data-testid="composer-body"
        />

        {/* Signatur-Vorschau */}
        {signatur && (
          <div className="p-2 bg-slate-50 rounded border border-slate-200 border-dashed" data-testid="composer-signatur-preview">
            <p className="text-[10px] text-slate-400 mb-0.5">Signatur (wird automatisch angehängt)</p>
            <pre className="text-xs text-slate-500 whitespace-pre-wrap font-sans leading-relaxed">{signatur.text}</pre>
          </div>
        )}

        {/* Senden */}
        <div className="flex items-center justify-between pt-1">
          <p className="text-[11px] text-slate-400">
            {anhaenge.length > 0 && `${anhaenge.length} Anhang/Anhänge · `}
            {text.length} Zeichen
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Verwerfen</Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={sending || !an.trim() || !betreff.trim() || konten.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="composer-send"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Send className="w-4 h-4 mr-1" />
              )}
              {sending ? 'Wird gesendet...' : 'Senden'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
